/**
 * @param {object} box
 * @param {number} nowCorrectedMs
 * @returns {'opened'|'locked'|'available'}
 */
export function boxState(box, nowCorrectedMs) {
    if (box.open_box_time !== null && box.open_box_time !== undefined) {
        return 'opened';
    }
    if (box.lockedUntilMs !== null && box.lockedUntilMs !== undefined && box.lockedUntilMs > nowCorrectedMs) {
        return 'locked';
    }
    return 'available';
}

/**
 * snapshot 的 box(locked_until 為秒)→ 前端內部形狀(lockedUntilMs 毫秒)
 * @param {object} rawBox
 * @returns {object}
 */
export function normalizeBox(rawBox) {
    const { locked_until, box_number, ...rest } = rawBox;
    return {
        ...rest,
        box_number: Number(box_number),
        lockedUntilMs: typeof locked_until === 'number' ? locked_until * 1000 : null,
    };
}

/**
 * @param {object[]} rawBoxes
 * @returns {object[]}
 */
export function normalizeBoxes(rawBoxes) {
    return rawBoxes.map(normalizeBox);
}

/**
 * Ably number_locked / number_unlocked(until 已是毫秒)
 * @param {object[]} boxes
 * @param {{boxNumbers: number[], untilMs: number|null, locked: boolean}} event
 * @returns {object[]}
 */
export function applyLockEvent(boxes, { boxNumbers, untilMs, locked }) {
    const targets = new Set(boxNumbers.map(Number));
    return boxes.map((b) => {
        if (!targets.has(b.box_number)) {
            return b;
        }
        return { ...b, lockedUntilMs: locked ? untilMs : null };
    });
}

/**
 * Ably product_drawn
 * @param {object[]} boxes
 * @param {object[]} drawnBoxes
 * @returns {object[]}
 */
export function applyDrawnEvent(boxes, drawnBoxes) {
    const byNumber = new Map(drawnBoxes.map((d) => [Number(d.box_number), d]));
    return boxes.map((b) => {
        const drawn = byNumber.get(b.box_number);
        if (!drawn) {
            return b;
        }
        return { ...b, open_box_time: drawn.open_box_time ?? Date.now(), box_level_name: drawn.box_level_name };
    });
}

/**
 * 從「已被移除」的號碼裡濾掉自己剛送出的那批。
 * 伺服器是先廣播開獎／鎖號才回 AJAX，送出的人一定會先收到自己那批號碼的事件，
 * 不濾掉就會對自己的號碼跳出「已被抽出或鎖定」。
 * @param {number[]} removed
 * @param {number[]} submitted 送出中、尚未收到自己請求結果的號碼
 * @returns {number[]}
 */
export function removedToNotify(removed, submitted) {
    const mine = new Set(submitted);
    return removed.filter((num) => !mine.has(num));
}

/**
 * @param {number[]} selection
 * @param {object[]} boxes
 * @param {number} nowCorrectedMs
 * @returns {{selection: number[], removed: number[]}}
 */
export function pruneSelection(selection, boxes, nowCorrectedMs) {
    const byNumber = new Map(boxes.map((b) => [b.box_number, b]));
    const kept = [];
    const removed = [];
    for (const num of selection) {
        const b = byNumber.get(num);
        if (b && boxState(b, nowCorrectedMs) === 'available') {
            kept.push(num);
        } else {
            removed.push(num);
        }
    }
    return { selection: kept, removed };
}

/**
 * 已抽出/鎖定不可選;無張數上限
 * @param {number[]} selection
 * @param {number} boxNumber
 * @param {object[]} boxes
 * @param {number} nowCorrectedMs
 * @returns {number[]}
 */
export function toggleSelection(selection, boxNumber, boxes, nowCorrectedMs) {
    const b = boxes.find((x) => x.box_number === boxNumber);
    if (!b || boxState(b, nowCorrectedMs) !== 'available') {
        return selection;
    }
    if (selection.includes(boxNumber)) {
        return selection.filter((n) => n !== boxNumber);
    }
    return [...selection, boxNumber];
}

/**
 * 可選的號碼，依號碼升冪——回傳的陣列順序就是開獎順位，不依賴傳入盤面的順序。
 *
 * @param {object[]} boxes
 * @param {number} nowCorrectedMs
 * @returns {number[]}
 */
export function selectAllAvailable(boxes, nowCorrectedMs) {
    return boxes
        .filter((b) => boxState(b, nowCorrectedMs) === 'available')
        .map((b) => b.box_number)
        .sort((a, b) => a - b);
}

/**
 * 校時後 offset 變動是否需要重繪盤面(只有作用中的鎖會受時間影響，已過期的鎖不算)
 * @param {number} previousOffsetMs
 * @param {number} nextOffsetMs
 * @param {object[]} boxes
 * @param {number} nowCorrectedMs
 * @returns {boolean}
 */
export function clockChangeAffectsBoard(previousOffsetMs, nextOffsetMs, boxes, nowCorrectedMs) {
    if (previousOffsetMs === nextOffsetMs) {
        return false;
    }
    return boxes.some((box) => box.lockedUntilMs !== null && box.lockedUntilMs > nowCorrectedMs);
}

/**
 * rand 注入以便決定性測試
 * @param {object[]} boxes
 * @param {number} count
 * @param {number} nowCorrectedMs
 * @param {function} rand
 * @returns {number[]}
 */
export function pickRandom(boxes, count, nowCorrectedMs, rand) {
    const available = boxes.filter((b) => boxState(b, nowCorrectedMs) === 'available').map((b) => b.box_number);
    const n = Math.min(count, available.length);
    const shuffled = available.slice();
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled.slice(0, n).sort((a, b) => a - b);
}
